import { formatReportingPeriod } from "./formatReportingPeriod";

const getColumnLetter = (colNum) => {
  let result = "";
  let num = colNum;
  while (num > 0) {
    num--;
    result = String.fromCharCode(65 + (num % 26)) + result;
    num = Math.floor(num / 26);
  }
  return result;
};

/**
 * Insert scorecard data into the active Excel worksheet starting at the
 * currently selected cell. Returns the range coordinates so the caller
 * can track them for later clearing.
 */
export const insertScorecardDataToExcel = async (scorecard, rubricName = "") => {
  return insertAllScorecardsToExcel([scorecard], rubricName);
};

export const SCORECARD_HEADERS = [
  "Rubric Id",
  "Rubric",
  "Company Id",
  "Company Name",
  "Framework",
  "Reporting Period",
  "Completed Status",
  "Category",
  "Category Score",
  "Category Max Score",
  "Category Weight",
  "Question Label",
  "Question Value",
  "Question Score",
  "Metric Reference",
  "Context",
];

const formatQuestionContext = (context) => {
  if (!context || !Array.isArray(context) || context.length === 0) {
    return "";
  }
  return context
    .map((entry) => (entry && entry.content != null ? String(entry.content).trim() : ""))
    .filter(Boolean)
    .join("\n\n");
};

const formatStatus = (raw) => {
  if (raw == null) return "";
  return String(raw)
    .replace(/^NOV\./i, "")
    .replace(/^Metric|^Report/i, "")
    .trim()
    .replace(/([a-z])([A-Z])/g, "$1 $2");
};

export const buildScorecardRows = (scorecard, rubricName) => {
  const rows = [];

  const rubricId = scorecard.rubric?.id || "";
  const companyId = scorecard.company?.id || "";
  const companyName = scorecard.company?.name || "";
  const frameworkName = scorecard.framework?.name || "";
  const reportingPeriod = formatReportingPeriod(
    scorecard.reportingPeriodStart,
    scorecard.reportingFrequency
  );
  const overallScore = scorecard.score ?? "";
  const isCompleted = formatStatus(scorecard.isCompleted);

  const summaryFields = [
    rubricId,
    rubricName,
    companyId,
    companyName,
    frameworkName,
    reportingPeriod,
    isCompleted,
  ];
  const emptyCategoryAndQuestionFields = ["", "", "", "", "", "", "", "", ""];

  if (scorecard.categories && scorecard.categories.length > 0) {
    scorecard.categories.forEach((category) => {
      const categoryFields = [
        category.name || "",
        category.score ?? "",
        category.maxScore ?? "",
        category.weight ?? "",
      ];

      if (category.questions && category.questions.length > 0) {
        category.questions.forEach((question) => {
          rows.push([
            ...summaryFields,
            ...categoryFields,
            question.label || "",
            question.value ?? "",
            question.score ?? "",
            question.metricReference ?? "",
            formatQuestionContext(question.context),
          ]);
        });
      } else {
        rows.push([...summaryFields, ...categoryFields, "", "", "", "", ""]);
      }
    });
  } else {
    rows.push([...summaryFields, ...emptyCategoryAndQuestionFields]);
  }

  const totalScoreFields = Array(SCORECARD_HEADERS.length).fill("");
  totalScoreFields[SCORECARD_HEADERS.indexOf("Rubric Id")] = rubricId;
  totalScoreFields[SCORECARD_HEADERS.indexOf("Rubric")] = rubricName;
  totalScoreFields[SCORECARD_HEADERS.indexOf("Company Id")] = companyId;
  totalScoreFields[SCORECARD_HEADERS.indexOf("Company Name")] = companyName;
  totalScoreFields[SCORECARD_HEADERS.indexOf("Framework")] = frameworkName;
  totalScoreFields[SCORECARD_HEADERS.indexOf("Reporting Period")] = reportingPeriod;
  const method = scorecard.aggregationMethod || "";
  totalScoreFields[SCORECARD_HEADERS.indexOf("Category")] = method
    ? `Total Score (${method})`
    : "Total Score";
  totalScoreFields[SCORECARD_HEADERS.indexOf("Category Score")] = overallScore;
  rows.push(totalScoreFields);

  return rows;
};

/**
 * Insert all scorecards for a rubric into the active Excel worksheet starting
 * at the currently selected cell. One header row, then all scorecards
 * sequentially. Returns the range coordinates for later clearing.
 */
export const insertAllScorecardsToExcel = async (scorecards, rubricName = "") => {
  if (!scorecards || scorecards.length === 0) {
    throw new Error("No scorecard data to insert");
  }

  return Excel.run(async (context) => {
    const sheet = context.workbook.worksheets.getActiveWorksheet();

    const selectedRange = context.workbook.getSelectedRange();
    selectedRange.load("rowIndex, columnIndex");
    await context.sync();

    const startRow = selectedRange.rowIndex + 1;
    const startColIndex = selectedRange.columnIndex;

    const rows = [SCORECARD_HEADERS];

    scorecards.forEach((scorecard) => {
      rows.push(...buildScorecardRows(scorecard, rubricName));
    });

    const startCol = getColumnLetter(startColIndex + 1);
    const endCol = getColumnLetter(startColIndex + SCORECARD_HEADERS.length);
    const endRow = startRow + rows.length - 1;

    const dataRange = sheet.getRange(`${startCol}${startRow}:${endCol}${endRow}`);
    dataRange.values = rows;

    const headerRange = sheet.getRange(`${startCol}${startRow}:${endCol}${startRow}`);
    headerRange.format.fill.color = "#1aebff";
    headerRange.format.font.color = "black";
    headerRange.format.font.bold = true;

    dataRange.format.autofitColumns();

    await context.sync();

    return { startCol, endCol, startRow, endRow };
  });
};

/**
 * Clear a previously inserted scorecard data range from the active worksheet.
 * No-ops if rangeInfo is null/undefined.
 */
export const clearScorecardDataRange = async (rangeInfo) => {
  if (!rangeInfo) return;

  await Excel.run(async (context) => {
    const sheet = context.workbook.worksheets.getActiveWorksheet();
    const { startCol, endCol, startRow, endRow } = rangeInfo;
    const fullRange = sheet.getRange(`${startCol}${startRow}:${endCol}${endRow}`);
    fullRange.clear(Excel.ClearApplyTo.all);
    await context.sync();
  });
};
